import { useLoader } from "@react-three/fiber";
import { useRef, useMemo } from "react";
import * as THREE from "three";
import { Text } from "@react-three/drei";
import { CardProps } from "../utils";
import { asset } from "../../consts";

const CardColor = '#ffffff';
const FrontPrimaryColor = '#dd3023';
const FrontSecondaryColor = '#fbf0c3';

// memo: 配信先サブパスに追従させた、Textコンポーネント用のフォントパス。
const FontPath = asset('/fonts/Huninn-Regular.ttf');

export default ({ id, position, rotation, isHovered, onMouseEnter, onMouseLeave, onClick }: CardProps) => {
  const meshRef = useRef<THREE.Mesh>(null);
  
  const rueTexture = useLoader(THREE.TextureLoader, asset('/images/card/rue-background.png'));
  const rueLogo = useLoader(THREE.TextureLoader, asset('/images/card/rue-logo.png'));
  const rueYanbarukuina = useLoader(THREE.TextureLoader, asset('/images/card/rue-yanbarukuina.png'));

  const materials = useMemo(() => {
    return [
      new THREE.MeshBasicMaterial({ color: CardColor  }),
      new THREE.MeshBasicMaterial({ color: CardColor  }),
      new THREE.MeshBasicMaterial({ color: CardColor  }),
      new THREE.MeshBasicMaterial({ color: CardColor  }),
      new THREE.MeshBasicMaterial({ map:   rueTexture }),
      new THREE.MeshBasicMaterial({ color: CardColor  }),
    ];
  }, [rueTexture]);

  return (
    <mesh
      ref={meshRef}
      material={materials}
      castShadow
      position={position ?? [0, 0, 0]}
      rotation={rotation ?? [0, 0, 0]}
      onPointerEnter={(e) => {
        e.stopPropagation();
        onMouseEnter?.();
      }}
      onPointerLeave={(e) => {
        e.stopPropagation();
        onMouseLeave?.();
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      <mesh position={[0, 0, 0]}><planeGeometry args={[0, 0]} /><meshBasicMaterial map={rueTexture}    transparent /></mesh>

      {/* 名刺サイズ: 91mm x 55mm : 1254px x 748px */}
      <boxGeometry args={[9.1, 5.5, 0.1]} />

      <group position={[0, 0, 0.051]} rotation={[0, 0, 0]}>
        <Text
          font={FontPath}
          color={FrontPrimaryColor}
          fontSize={0.175}
          position={[-3.85, 2.1, 0]}
          anchorX={'left'}
        >
          開発グループ
        </Text>
        <Text
          font={FontPath}
          color={FrontPrimaryColor}
          fontSize={0.175}
          position={[-3.85, 1.9, 0]}
          anchorX={'left'}
        >
          エンジニア
        </Text>
        <Text
          font={FontPath}
          color={FrontPrimaryColor}
          fontSize={0.6}
          position={[-3.9, 1.5, 0]}
          anchorX={'left'}
        >
          屋良 祐利
        </Text>
        <Text
          font={FontPath}
          color={FrontPrimaryColor}
          fontSize={0.175}
          position={[-3.5, 0.95, 0]}
          anchorX={'left'}
          onClick={(e) => {
            e.stopPropagation();
            window.open('mailto:yara.yuto@rue.co.jp');
          }}
          onPointerEnter={(e) => {
            e.stopPropagation();
            document.body.style.cursor = 'pointer';
          }}
          onPointerLeave={(e) => {
            e.stopPropagation();
            document.body.style.cursor = 'default';
          }}
        >
          Mail. yara.yuto@rue.co.jp
        </Text>

        <mesh position={[3.5, 1.8, 0.01]}>
          <planeGeometry args={[1.1, 1.3]} />
          <meshBasicMaterial map={rueLogo} transparent />
        </mesh>

        <mesh position={[-3, -0.225, 0.01]}>
          <planeGeometry args={[0.75, 0.75]} />
          <meshBasicMaterial map={rueYanbarukuina} transparent />
        </mesh>

        <Text
          font={FontPath}
          color={FrontSecondaryColor}
          fontSize={0.4}
          position={[-3.875, -1, 0]}
          anchorX={'left'}
        >
          株式会社りゅう
        </Text>
        <Text
          font={FontPath}
          color={FrontSecondaryColor}
          fontSize={0.175}
          position={[-3.5, -1.5, 0]}
          anchorX={'left'}
          onClick={(e) => {
            e.stopPropagation();
            window.open('tel:0989176431');
          }}
          onPointerEnter={(e) => {
            e.stopPropagation();
            document.body.style.cursor = 'pointer';
          }}
          onPointerLeave={(e) => {
            e.stopPropagation();
            document.body.style.cursor = 'default';
          }}
        >
          Tel. 098-917-6431
        </Text>
        <Text
          font={FontPath}
          color={FrontSecondaryColor}
          fontSize={0.175}
          position={[-3.5, -1.7, 0]}
          anchorX={'left'}
          onClick={(e) => {
            e.stopPropagation();
            window.open('https://www.rue.co.jp', '_blank');
          }}
          onPointerEnter={(e) => {
            e.stopPropagation();
            document.body.style.cursor = 'pointer';
          }}
          onPointerLeave={(e) => {
            e.stopPropagation();
            document.body.style.cursor = 'default';
          }}
        >
          Site. https://www.rue.co.jp
        </Text>

        <Text
          font={FontPath}
          color={FrontSecondaryColor}
          fontSize={0.2}
          position={[-0.125, -0.925, 0]}
          anchorX={'left'}
        >
          【本社】
        </Text>
        <Text
          font={FontPath}
          color={FrontSecondaryColor}
          fontSize={0.175}
          position={[0, -1.195, 0]}
          anchorX={'left'}
        >
          〒900-0004
        </Text>
        <Text
          font={FontPath}
          color={FrontSecondaryColor}
          fontSize={0.175}
          position={[0, -1.395, 0]}
          anchorX={'left'}
        >
          沖縄県那覇市銘苅2-3-6 那覇市IT創造館301号室
        </Text>
        <Text
          font={FontPath}
          color={FrontSecondaryColor}
          fontSize={0.2}
          position={[-0.125, -1.75, 0]}
          anchorX={'left'}
        >
          【東京営業所】
        </Text>
        <Text
          font={FontPath}
          color={FrontSecondaryColor}
          fontSize={0.175}
          position={[0, -2.02, 0]}
          anchorX={'left'}
        >
          〒160-0015
        </Text>
        <Text
          font={FontPath}
          color={FrontSecondaryColor}
          fontSize={0.175}
          position={[0, -2.22, 0]}
          anchorX={'left'}
        >
          東京都新宿区大京町22-2 大京町JPビル5F
        </Text>
      </group>
    </mesh>
  );
};
